export const PLANNED_PRICES = Object.freeze({
  monthly: { amount: 2.99, currency: 'USD', interval: 'month' },
  yearly: { amount: 29.99, currency: 'USD', interval: 'year' },
});
export const DISABLED_BILLING = Object.freeze({ enabled: false, prices: PLANNED_PRICES, trialDays: 7 });
const ACCOUNT_ORIGINS = new Set(['https://peacetech.app', 'https://www.peacetech.app']);
export const ACCOUNT_PATH = '/tutti-helper/account';
export const TEST_ACCOUNT_PATH = '/tutti-helper/test/account';

export function billingBase(value = process.env.REACT_APP_TUTTI_BILLING_API) {
  if (!value) return null;
  try {
    const url = new URL(value);
    if (url.protocol !== 'https:' || url.username || url.password || url.search || url.hash) return null;
    return url.href.replace(/\/$/, '');
  } catch { return null; }
}

export function billingError(code = 'unavailable') { return Object.assign(new Error('Membership request could not be completed.'), { code }); }

export function accountBillingBase(accountPath = ACCOUNT_PATH) {
  if (accountPath === TEST_ACCOUNT_PATH) return billingBase(process.env.REACT_APP_TUTTI_TEST_BILLING_API || '');
  if (accountPath === ACCOUNT_PATH) return billingBase();
  return null;
}

export function accountExtensionIds(accountPath = ACCOUNT_PATH) {
  if (accountPath === TEST_ACCOUNT_PATH) return allowedExtensionIds(process.env.REACT_APP_TUTTI_TEST_EXTENSION_IDS || '');
  if (accountPath === ACCOUNT_PATH) return allowedExtensionIds();
  return new Set();
}

async function boundedRequest(operation, externalSignal) {
  const controller = new AbortController();
  let timer;
  let abort;
  const deadline = new Promise((_, reject) => {
    abort = () => { controller.abort(); reject(new DOMException('Request canceled.', 'AbortError')); };
    if (externalSignal?.aborted) { abort(); return; }
    externalSignal?.addEventListener('abort', abort, { once: true });
    timer = setTimeout(() => { reject(billingError('request_timeout')); controller.abort(); }, 20000);
  });
  try { return await Promise.race([deadline, operation(controller.signal)]); }
  finally { clearTimeout(timer); externalSignal?.removeEventListener('abort', abort); }
}

export async function billingRequest(path, { user, method = 'GET', body, signal, accountPath = ACCOUNT_PATH } = {}) {
  const base = accountBillingBase(accountPath);
  if (!base) throw billingError('billing_disabled');
  return boundedRequest(async requestSignal => {
    if (requestSignal.aborted) throw new DOMException('Request canceled.', 'AbortError');
    const headers = { Accept: 'application/json' };
    // Let Firebase reuse a valid token and refresh it when expiry requires it.
    // The backend independently verifies the token and checks revocation.
    if (user) headers.Authorization = `Bearer ${await user.getIdToken()}`;
    // Firebase token refresh cannot be aborted. Never start a late request after
    // the caller has canceled or the total operation has timed out.
    if (requestSignal.aborted) throw new DOMException('Request canceled.', 'AbortError');
    if (body) headers['Content-Type'] = 'application/json';
    const response = await fetch(`${base}${path}`, { method, headers, body: body ? JSON.stringify(body) : undefined, credentials: 'omit', cache: 'no-store', redirect: 'error', signal: requestSignal });
    let result;
    try { result = await response.json(); } catch { throw billingError('invalid_response'); }
    if (!response.ok) throw billingError(typeof result?.error?.code === 'string' ? result.error.code : 'unavailable');
    return result;
  }, signal);
}

export function validatedConfig(value, accountPath = ACCOUNT_PATH) {
  const mode = value?.mode;
  if (accountPath === TEST_ACCOUNT_PATH && mode !== 'test') throw billingError('test_mode_required');
  if (mode !== undefined && mode !== 'test' && mode !== 'live') throw billingError('invalid_response');
  // Older endpoints omit mode. Never infer a test environment from a URL or
  // from billing being disabled: only an explicit server value warrants it.
  const environment = mode === undefined ? {} : { mode };
  if (value?.enabled !== true) return mode === undefined ? DISABLED_BILLING : { ...DISABLED_BILLING, ...environment };
  for (const [plan, interval] of [['monthly', 'month'], ['yearly', 'year']]) {
    const price = value.prices?.[plan];
    if (!price || !Number.isFinite(price.amount) || price.amount <= 0 || price.currency !== 'USD' || price.interval !== interval) throw billingError('invalid_response');
  }
  if (value.trialDays !== 7) throw billingError('invalid_response');
  return { enabled: true, prices: value.prices, trialDays: 7, ...environment };
}

export function validatedEntitlement(value) {
  const statuses = ['none', 'trialing', 'active', 'expired', 'past_due', 'canceled'];
  if (!statuses.includes(value?.status) || typeof value.hasAccess !== 'boolean' || typeof value.trialEligible !== 'boolean' || ![null, 'monthly', 'yearly'].includes(value.plan) || typeof value.cancelAtPeriodEnd !== 'boolean' || !Number.isFinite(Date.parse(value.serverTime))) throw billingError('invalid_response');
  for (const key of ['trialEndsAt', 'currentPeriodEndsAt']) if (value[key] !== null && !Number.isFinite(Date.parse(value[key]))) throw billingError('invalid_response');
  return value;
}

export function stripeDestination(value, kind) {
  try {
    const url = new URL(value);
    const host = kind === 'portal' ? 'billing.stripe.com' : 'checkout.stripe.com';
    if (url.protocol === 'https:' && url.hostname === host && !url.port && !url.username && !url.password) return url.href;
  } catch { /* Reject every unrecognized redirect. */ }
  throw billingError('invalid_redirect');
}

export function allowedExtensionIds(value = process.env.REACT_APP_TUTTI_EXTENSION_IDS) {
  return new Set((value || '').split(',').map(id => id.trim()).filter(id => /^[a-p]{32}$/.test(id)));
}

export function extensionRequest(location) {
  try {
    const url = new URL(location);
    if (!ACCOUNT_ORIGINS.has(url.origin) || ![ACCOUNT_PATH, TEST_ACCOUNT_PATH].includes(url.pathname)) return null;
    if (url.searchParams.getAll('extension_id').length !== 1 || url.searchParams.getAll('nonce').length !== 1) return null;
    const extensionId = url.searchParams.get('extension_id');
    const nonce = url.searchParams.get('nonce');
    return accountExtensionIds(url.pathname).has(extensionId) && /^[a-f0-9]{64}$/.test(nonce || '') ? { extensionId, nonce, accountPath: url.pathname } : null;
  } catch { return null; }
}

export function verifiedGoogleUser(user) {
  return Boolean(user?.emailVerified && user.providerData?.some(provider => provider.providerId === 'google.com'));
}

export async function connectExtension(user, request, runtime, consent) {
  if (!consent || !verifiedGoogleUser(user) || !request || !runtime?.sendMessage) throw billingError('extension_unavailable');
  // Revalidate the destination at the action boundary; the query never selects
  // an API endpoint and no page load sends credentials on the user's behalf.
  const current = extensionRequest(window.location.href);
  if (!current || current.accountPath !== request.accountPath || current.extensionId !== request.extensionId || current.nonce !== request.nonce) throw billingError('extension_unavailable');
  return boundedRequest(async signal => {
    const idToken = await user.getIdToken(true);
    if (signal.aborted) throw billingError('request_timeout');
    return new Promise((resolve, reject) => {
      try {
        runtime.sendMessage(request.extensionId, { type: 'PEACETECH_AUTH', nonce: request.nonce, idToken }, response => {
          if (runtime.lastError || response?.ok !== true) {
            if (response?.code === 'test_account_not_allowed') {
              reject(billingError('test_account_not_allowed'));
              return;
            }
            const recognized = ['invalid_request', 'expired_request', 'canceled_request', 'connection_failed'].includes(response?.code);
            reject(billingError(recognized ? `extension_${response.code}` : 'extension_unavailable'));
          }
          else resolve();
        });
      } catch { reject(billingError('extension_unavailable')); }
    });
  });
}
